import { useRouter } from 'next/router';
import { useLang } from '@/i18n';
import EcoImg from 'eco-design-components/base/eco-img';
import EcoSpace from 'eco-design-components/base/eco-space';
import { useState, useEffect } from 'react';
import css from './index.module.scss';
import { getSolarRecommand } from '@/service/solar-calculator';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, EffectFade } from 'swiper';
import { Swiper as SwiperType } from 'swiper/types';
import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import EcoLink from 'eco-design/base/eco-link';
import { Amazon } from './icons';
import Dialog from './Dialog';
import * as imgs from './imgs';
import gtm from '@/utils/gtm';
import { useScheduledContent } from '@/utils/hooks/useScheduledContent';

interface ItemType {
    imgs: string[];
    img?: string;
    title?: string;
    productId: string;
    variantId: string;
    price?: number;
    compareAtPrice?: number;
    inventoryQuantity?: number;
    inventoryPolicy?: 'deny' | 'continue' | undefined;
    btnType?: 'buy' | 'cart' | 'more';
    linkMore: string;
    linkBuy: string;
    linkAmazon?: string;
    isPreSale?: boolean;
    NotOnlineYet?: boolean;
    info: {
        title: string;
        desc: string;
    }[];
}

const C: React.FC = () => {
    const { locale = 'us' } = useRouter();
    const { t } = useLang('rapidpro');
    const IS_EU: boolean = ['eu', 'uk', 'de', 'fr', 'it', 'es', 'nl'].includes(locale);
    const IS_UK: boolean = ['uk'].includes(locale);
    const IS_IT = ['it'].includes(locale);
    const IS_NL = ['nl'].includes(locale);
    const IS_JP: boolean = ['jp'].includes(locale);
    const IS_NA: boolean = ['us', 'ca'].includes(locale);
    const IS_RW: boolean = ['ph'].includes(locale);
    const IS_PH: boolean = ['ph'].includes(locale);
    const { img1, img2, img3, img4, img5, img8 } = imgs;
    const img6 = IS_UK ? imgs.img6.uk : IS_EU ? imgs.img6.eu : imgs.img6.us;
    const img7 = IS_UK ? imgs.img7.uk : IS_EU ? imgs.img7.eu : imgs.img7.us;
    let data: ItemType[] = [
        {
            imgs: ['', img1[0], img1[1]],
            productId: (() => {
                const localMap: any = {
                    us: '15361630044233',
                    ca: '15126335881587',
                    jp: '14715284160879',
                    eu: '10057124348247',
                    uk: '9922524315987',
                    de: '15246671348093',
                    fr: '9916298592603',
                    it: '14929760059768',
                    es: '9916308980059',
                };
                return localMap[locale] || undefined;
            })(),
            variantId: (() => {
                const localMap: any = {
                    us: '54593875509321',
                    ca: '52731600175475',
                    jp: '51993088622959',
                    eu: '50740155875671',
                    uk: '51047157825875',
                    de: '56076475171197',
                    fr: '50627140485467',
                    it: '55140789584248',
                    es: '50627174367579',
                };
                return localMap[locale] || undefined;
            })(),
            linkMore: (() => {
                const localMap: any = {
                    us: 'https://us.ecoflow.com/products/ecoflow-rapid-pro-x-power-bank-27650mah-300w',
                    ca: 'https://ca.ecoflow.com/products/ecoflow-rapid-pro-x-power-bank-27-650mah-300w',
                    jp: 'https://jp.ecoflow.com/products/rapid-pro-x-power-bank-27650mah-140w',
                    eu: 'https://eu.ecoflow.com/products/rapid-pro-x-power-bank',
                    uk: 'https://uk.ecoflow.com/products/rapid-pro-x-power-bank',
                    de: 'https://de.ecoflow.com/products/rapid-pro-x-power-bank',
                    fr: 'https://fr.ecoflow.com/products/rapid-pro-x-power-bank',
                    it: 'https://it.ecoflow.com/products/rapid-pro-x-power-bank',
                    es: 'https://es.ecoflow.com/products/rapid-pro-x-power-bank',
                };
                return localMap[locale] || undefined;
            })(),
            linkBuy: '',
            linkAmazon: (() => {
                const localeMap: any = {
                    us: 'https://www.amazon.com/dp/B0FHB5HZFR?maas=maas_adg_CD09AE1204CC53D55F9ED39C1164B2E7_afap_abs&ref_=aa_maas&tag=maas',
                    ca: 'https://www.amazon.ca/dp/B0FHB5HZFR?maas=maas_adg_29D19135EDA8CBCBBD3726F875609315_afap_abs&ref_=aa_maas&tag=maas',
                };
                return localeMap[locale] || undefined;
            })(),
            // linkAmazon: IS_JP ? 'https://www.amazon.co.jp/dp/B0FCXYVLNH' : '',
            info: [
                { title: t('C_item1_info1_title'), desc: t('C_item1_info1_desc') },
                { title: t('C_item1_info2_title'), desc: t('C_item1_info2_desc') },
                { title: t('C_item1_info3_title'), desc: t('C_item1_info3_desc') },
            ],
            // NotOnlineYet: IS_NA,
        },
        {
            imgs: ['', img2[0], img2[1]],
            productId: (() => {
                const localMap: any = {
                    us: '15361638924361',
                    ca: '15126336274803',
                    jp: '14715334492527',
                    eu: '10057125331287',
                    uk: '9922529395027',
                    de: '15246672003453',
                    fr: '9916298821979',
                    it: '14929760158072',
                    es: '9916309569883',
                    ph: '10322678251830',
                    nl: '9935713599764',
                };
                return localMap[locale] || undefined;
            })(),
            variantId: (() => {
                const localMap: any = {
                    us: '54593876000841',
                    ca: '52731601256819',
                    jp: '51993093767535',
                    eu: '50740157382999',
                    uk: '51047163396435',
                    de: '56076476449149',
                    fr: '50627140813147',
                    it: '55140789846392',
                    es: '50627176595803',
                    ph: '52941429604662',
                    nl: '51194425377044',
                };
                return localMap[locale] || undefined;
            })(),
            linkMore: (() => {
                const localMap: any = {
                    us: 'https://us.ecoflow.com/products/ecoflow-rapid-pro-power-bank-27650mah-300w-140w-built-in-cable',
                    ca: 'https://ca.ecoflow.com/products/ecoflow-rapid-pro-power-bank-27-650mah-300w-140w-built-in-cable',
                    jp: 'https://jp.ecoflow.com/products/rapid-pro-power-bank-27650mah-300w-140w-usb-c',
                    eu: 'https://eu.ecoflow.com/products/rapid-pro-27k-power-bank',
                    uk: 'https://uk.ecoflow.com/products/rapid-pro-27k-power-bank',
                    de: 'https://de.ecoflow.com/products/rapid-pro-27k-power-bank',
                    fr: 'https://fr.ecoflow.com/products/rapid-pro-27k-power-bank',
                    it: 'https://it.ecoflow.com/products/rapid-pro-27k-power-bank',
                    es: 'https://es.ecoflow.com/products/rapid-pro-27k-power-bank',
                    nl: 'https://nl.ecoflow.com/products/rapid-pro-27k-power-bank',
                    ph: 'https://ph.ecoflow.com/products/ecoflow-rapid-pro-power-bank-27650mah-300w-140w-built-in-cable',
                };
                return localMap[locale] || undefined;
            })(),
            linkBuy: 'https://www.amazon.co.jp/dp/B0FCXYL595',
            linkAmazon: (() => {
                const localeMap: any = {
                    us: 'https://www.amazon.com/dp/B0FHB6HFPJ?maas=maas_adg_FD3233A7CC93AB0ACF59686EE97E625F_afap_abs&ref_=aa_maas&tag=maas',
                    ca: 'https://www.amazon.ca/dp/B0FHB6HFPJ?maas=maas_adg_C83E3D6FE9A00B8DB1ACD4DE640B696B_afap_abs&ref_=aa_maas&tag=maas',
                };
                return localeMap[locale] || undefined;
            })(),
            // linkAmazon: IS_JP ? 'https://www.amazon.co.jp/dp/B0FCXYL595' : '',
            info: [
                { title: t('C_item2_info1_title'), desc: t('C_item2_info1_desc') },
                { title: t('C_item2_info2_title'), desc: t('C_item2_info2_desc') },
                { title: t('C_item2_info3_title'), desc: t('C_item2_info3_desc') },
            ],
        },
        {
            imgs: ['', img3[0], img3[1]],
            productId: (() => {
                const localMap: any = {
                    us: '15361639481417',
                    ca: '15126336504179',
                    jp: '14715335082351',
                    eu: '10057125888343',
                    uk: '9922534441299',
                    de: '15246672593277',
                    fr: '9916299051355',
                    it: '14929760223608',
                    es: '9916309864795',
                    ph: '10322678317366',
                    nl: '9935713632532',
                };
                return localMap[locale] || undefined;
            })(),
            variantId: (() => {
                const localMap: any = {
                    us: '54627329474633',
                    ca: '52731601584499',
                    jp: '51993096356207',
                    eu: '50740158071127',
                    uk: '51047172211027',
                    de: '56076477432189',
                    fr: '50627141402971',
                    it: '55140789944696',
                    es: '50627177054555',
                    ph: '52941429702966',
                    nl: '51194425147668',
                };
                return localMap[locale] || undefined;
            })(),
            linkMore: (() => {
                const localMap: any = {
                    us: 'https://us.ecoflow.com/products/ecoflow-rapid-pro-power-bank-20000mah-230w-140w-built-in-cable',
                    ca: 'https://ca.ecoflow.com/products/ecoflow-rapid-pro-power-bank20-000mah-230w-140w-built-in-cable',
                    jp: 'https://jp.ecoflow.com/products/rapid-pro-power-bank-20000mah-230w-100w-usb-c',
                    eu: 'https://eu.ecoflow.com/products/rapid-pro-20k-power-bank',
                    uk: 'https://uk.ecoflow.com/products/rapid-pro-20k-power-bank',
                    de: 'https://de.ecoflow.com/products/rapid-pro-20k-power-bank',
                    fr: 'https://fr.ecoflow.com/products/rapid-pro-20k-power-bank',
                    it: 'https://it.ecoflow.com/products/rapid-pro-20k-power-bank',
                    es: 'https://es.ecoflow.com/products/rapid-pro-20k-power-bank',
                    nl: 'https://nl.ecoflow.com/products/rapid-pro-20k-power-bank',
                    ph: 'https://ph.ecoflow.com/products/ecoflow-rapid-pro-power-bank-20000mah-230w-100w-built-in-cable',
                };
                return localMap[locale] || undefined;
            })(),
            linkBuy: '',
            linkAmazon: (() => {
                const localeMap: any = {
                    us: 'https://www.amazon.com/dp/B0FHB6W4YH?maas=maas_adg_4C211BAC7C42D80F5704130BBF05E0F9_afap_abs&ref_=aa_maas&tag=maas',
                    ca: 'https://www.amazon.ca/dp/B0FHB6W4YH?maas=maas_adg_FECB88E02297E2A76866E93D55F44031_afap_abs&ref_=aa_maas&tag=maas',
                };
                return localeMap[locale] || undefined;
            })(),
            // linkAmazon: IS_JP ? 'https://www.amazon.co.jp/dp/B0FCXQPDHV' : '',
            info: [
                { title: t('C_item3_info1_title'), desc: t('C_item3_info1_desc') },
                { title: t('C_item3_info2_title'), desc: t('C_item3_info2_desc') },
                { title: t('C_item3_info3_title'), desc: t('C_item3_info3_desc') },
            ],
        },
        {
            imgs: ['', IS_EU ? img5[0] : img4[0], IS_EU ? img5[1] : img4[1]],
            productId: (() => {
                const localMap: any = {
                    us: '15361639809097',
                    ca: '15126339551603',
                    jp: '14715335573871',
                    eu: '10057126576471',
                    uk: '52731615412595',
                    de: '15246672953725',
                    fr: '9916299346267',
                    it: '14929760321912',
                    es: '9916310159707',
                    ph: '10304284950838',
                    nl: '9935713665300',
                };
                return localMap[locale] || undefined;
            })(),
            variantId: (() => {
                const localMap: any = {
                    us: '54593877606473',
                    ca: '52731615412595',
                    jp: '51993101631855',
                    eu: '50740159218007',
                    uk: '',
                    de: '56076478447997',
                    fr: '50627143565659',
                    it: '55140790043000',
                    es: '50627177415003',
                    ph: '52748208537910',
                    nl: '51194425213204',
                };
                return localMap[locale] || undefined;
            })(),
            linkMore: (() => {
                const localMap: any = {
                    us: 'https://us.ecoflow.com/products/ecoflow-rapid-pro-3-in-1-power-bank-10000mah-67w',
                    ca: 'https://ca.ecoflow.com/products/ecoflow-rapid-pro-3-in-1-power-bank-10-000mah-67w',
                    jp: 'https://jp.ecoflow.com/products/rapid-pro-3-in-1-power-bank-10000mah-67w',
                    eu: 'https://eu.ecoflow.com/products/rapid-pro--desktop-charger-320w',
                    uk: '',
                    de: 'https://de.ecoflow.com/products/rapid-pro--desktop-charger-320w',
                    fr: 'https://fr.ecoflow.com/products/rapid-pro--desktop-charger-320w',
                    it: 'https://it.ecoflow.com/products/rapid-pro--desktop-charger-320w',
                    es: 'https://es.ecoflow.com/products/rapid-pro--desktop-charger-320w',
                    nl: 'https://nl.ecoflow.com/products/rapid-pro--desktop-charger-320w',
                    ph: 'https://ph.ecoflow.com/products/ecoflow-rapid-pro-3-in-1-power-bank-10000mah-67w',
                };
                return localMap[locale] || undefined;
            })(),
            linkBuy: '',
            // isPreSale: IS_JP,
            linkAmazon: (() => {
                const localeMap: any = {
                    jp: 'https://www.amazon.co.jp/dp/B0FCXDX85Z',
                    us: 'https://www.amazon.com/dp/B0FHB84B41?maas=maas_adg_288DB6CD35DFE832DFF6721E9FADDB71_afap_abs&ref_=aa_maas&tag=maas',
                    ca: 'https://www.amazon.ca/dp/B0FHB84B41?maas=maas_adg_1A6297FC77A31FBBFB9CDF37A3674808_afap_abs&ref_=aa_maas&tag=maas',
                };
                return localeMap[locale] || undefined;
            })(),
            // NotOnlineYet: IS_JP,
            info: IS_EU
                ? [
                      { title: t('C_item4_info1_title'), desc: t('C_item4_info1_desc') },
                      { title: t('C_item4_info2_title'), desc: t('C_item4_info2_desc') },
                      { title: t('C_item4_info3_title'), desc: t('C_item4_info3_desc') },
                      { title: t('C_item4_info4_title'), desc: t('C_item4_info4_desc') },
                  ]
                : [
                      { title: t('C_item4_info1_title'), desc: t('C_item4_info1_desc') },
                      { title: t('C_item4_info2_title'), desc: t('C_item4_info2_desc') },
                      { title: t('C_item4_info3_title'), desc: t('C_item4_info3_desc') },
                  ],
        },
        {
            img: '',
            imgs: IS_EU ? ['', img6[0]] : ['', img5[0], img5[1]],
            productId: (() => {
                const localMap: any = {
                    // us: '15361640038473',
                    // ca: '15126340338035',
                    jp: '14715335737711',
                    eu: '10057127461207',
                    uk: '9922538537299',
                    de: '15246673346941',
                    fr: '9916299673947',
                    it: '14929760387448',
                    es: '9916310618459',
                    nl: '9935713763604',
                };
                return localMap[locale] || undefined;
            })(),
            variantId: (() => {
                const localMap: any = {
                    // us: '54593877868617',
                    // ca: '52731616526707',
                    jp: '51993102123375',
                    eu: '50740946665815',
                    uk: '51048656929107',
                    de: '56082550456701',
                    fr: '50630580568411',
                    it: '55141746114936',
                    es: '50630604882267',
                    nl: '51194425245972',
                };
                return localMap[locale] || undefined;
            })(),
            linkMore: (() => {
                const localMap: any = {
                    us: 'https://us.ecoflow.com/products/ecoflow-rapid-pro-desktop-charger-320w-5-ports-gan',
                    ca: 'https://ca.ecoflow.com/products/ecoflow-rapid-pro-desktop-charger-320w-5-ports-gan',
                    jp: 'https://jp.ecoflow.com/products/rapid-pro-320w-gan',
                    eu: 'https://eu.ecoflow.com/products/rapid-pro-140w-multi-functional-charger-4-ports-usb-c-power-delivery',
                    uk: 'https://uk.ecoflow.com/products/rapid-pro-140w-multi-functional-charger-4-ports-usb-c-power-delivery',
                    de: 'https://de.ecoflow.com/products/rapid-pro-140w-multi-functional-charger-4-ports-usb-c-power-delivery',
                    fr: 'https://fr.ecoflow.com/products/rapid-pro-140w-multi-functional-charger-4-ports-usb-c-power-delivery',
                    it: 'https://it.ecoflow.com/products/rapid-pro-140w-multi-functional-charger-4-ports-usb-c-power-delivery',
                    es: 'https://es.ecoflow.com/products/rapid-pro-140w-multi-functional-charger-4-ports-usb-c-power-delivery',
                    nl: 'https://nl.ecoflow.com/products/rapid-pro-140w-multi-functional-charger-4-ports-usb-c-power-delivery',
                };
                return localMap[locale] || undefined;
            })(),
            linkBuy: '',
            linkAmazon: (() => {
                const localMap: any = {
                    us: 'https://www.amazon.com/dp/B0FHB832TL?maas=maas_adg_E14996396050A60C43EA71EF35C061D2_afap_abs&ref_=aa_maas&tag=maas',
                    ca: 'https://www.amazon.ca/dp/B0FHB832TL?maas=maas_adg_3DD070B6A6D9D703CFFC9B4A452CA8B7_afap_abs&ref_=aa_maas&tag=maas',
                    // jp: 'https://www.amazon.co.jp/dp/B0FCY5GGY2',
                };
                return localMap[locale] || undefined;
            })(),
            // NotOnlineYet: IS_JP,
            // isPreSale: IS_JP,
            info: IS_EU
                ? [
                      { title: t('C_item5_info1_title'), desc: t('C_item5_info1_desc') },
                      { title: t('C_item5_info2_title'), desc: t('C_item5_info2_desc') },
                      { title: t('C_item5_info3_title'), desc: t('C_item5_info3_desc') },
                  ]
                : [
                      { title: t('C_item5_info1_title'), desc: t('C_item5_info1_desc') },
                      { title: t('C_item5_info2_title'), desc: t('C_item5_info2_desc') },
                      { title: t('C_item5_info3_title'), desc: t('C_item5_info3_desc') },
                      { title: t('C_item5_info4_title'), desc: t('C_item5_info4_desc') },
                  ],
        },
        {
            img: '',
            imgs: IS_EU ? ['', img7[0]] : ['', img6[0]],
            productId: (() => {
                const localMap: any = {
                    us: '15361640366153',
                    ca: '15126340993395',
                    jp: '14715335934319',
                    eu: '10057127133527',
                    uk: '9922535293267',
                    de: '15246673150333',
                    fr: '9916299542875',
                    it: '14929760354680',
                    es: '9916310421851',
                    nl: '9935713698068',
                };
                return localMap[locale] || undefined;
            })(),
            variantId: (() => {
                const localMap: any = {
                    us: '54593879081033',
                    ca: '52731617083763',
                    jp: '51993103761775',
                    eu: '50740949844311',
                    uk: '51048660238675',
                    de: '56082564645245',
                    fr: '50630573523291',
                    it: '55141747196280',
                    es: '50630611468635',
                    nl: '51194425082132',
                };
                return localMap[locale] || undefined;
            })(),
            linkMore: (() => {
                const localMap: any = {
                    us: 'https://us.ecoflow.com/products/ecoflow-rapid-pro-charger-140w-4-ports-gan',
                    ca: 'https://ca.ecoflow.com/products/ecoflow-rapid-pro-charger-140w-4-ports-gan',
                    jp: 'https://jp.ecoflow.com/products/rapid-pro-140w-gan',
                    eu: 'https://eu.ecoflow.com/products/rapid-pro-100w-multi-functional-charger-3-ports-usb-c-power-delivery',
                    uk: 'https://uk.ecoflow.com/products/rapid-pro-100w-multi-functional-charger-3-ports-usb-c-power-delivery',
                    de: 'https://de.ecoflow.com/products/rapid-pro-100w-multi-functional-charger-3-ports-usb-c-power-delivery',
                    fr: 'https://fr.ecoflow.com/products/rapid-pro-100w-multi-functional-charger-3-ports-usb-c-power-delivery',
                    it: 'https://it.ecoflow.com/products/rapid-pro-100w-multi-functional-charger-3-ports-usb-c-power-delivery',
                    es: 'https://es.ecoflow.com/products/rapid-pro-100w-multi-functional-charger-3-ports-usb-c-power-delivery',
                    nl: 'https://nl.ecoflow.com/products/rapid-pro-100w-multi-functional-charger-3-ports-usb-c-power-delivery',
                };
                return localMap[locale] || undefined;
            })(),
            linkBuy: '',
            linkAmazon: (() => {
                const localMap: any = {
                    jp: 'https://www.amazon.co.jp/dp/B0FCY1PZ33?th=1',
                    us: 'https://www.amazon.com/dp/B0FDGTG8HP?maas=maas_adg_F4FE3C33C3D1C2D1BEEEBE1801D57C24_afap_abs&ref_=aa_maas&tag=maas',
                    ca: 'https://www.amazon.ca/dp/B0FDGTG8HP?maas=maas_adg_F64DA7D2272318F567A83759CBF03CD1_afap_abs&ref_=aa_maas&tag=maas',
                };
                return localMap[locale] || undefined;
            })(),
            info: [
                { title: t('C_item6_info1_title'), desc: t('C_item6_info1_desc') },
                { title: t('C_item6_info2_title'), desc: t('C_item6_info2_desc') },
                { title: t('C_item6_info3_title'), desc: t('C_item6_info3_desc') },
            ],
        },
        {
            img: '',
            imgs: IS_EU ? ['', img8[0], img8[1]] : ['', img7[0]],
            productId: (() => {
                const localMap: any = {
                    us: '15361640595529',
                    ca: '15126348366195',
                    jp: '14715336130927',
                    eu: '10057127821655',
                    uk: '9922541584723',
                    de: '15246673576317',
                    fr: '9916299903323',
                    it: '14929760452984',
                    es: '9916311339355',
                    nl: '9935713730836',
                };
                return localMap[locale] || undefined;
            })(),
            variantId: (() => {
                const localMap: any = {
                    us: '54593879310409',
                    ca: '52731619410291',
                    jp: '51993104941423',
                    eu: '50740161610071',
                    uk: '51047180566867',
                    de: '56076480184701',
                    fr: '50627144974683',
                    it: '55140790206840',
                    es: '50627178725723',
                    nl: '51194425180436',
                };
                return localMap[locale] || undefined;
            })(),
            linkMore: (() => {
                const localMap: any = {
                    us: 'https://us.ecoflow.com/products/ecoflow-rapid-pro-charger-100w-3-ports-gan',
                    ca: 'https://ca.ecoflow.com/products/ecoflow-rapid-pro-charger-100w-3-ports-gan',
                    jp: 'https://jp.ecoflow.com/products/rapid-pro-100w-gan',
                    eu: 'https://eu.ecoflow.com/products/rapid-pro-c-c-cable-240w-power-output',
                    uk: 'https://uk.ecoflow.com/products/rapid-pro-c-c-cable-240w-power-output',
                    de: 'https://de.ecoflow.com/products/rapid-pro-c-c-cable-240w-power-output',
                    fr: 'https://fr.ecoflow.com/products/rapid-pro-c-c-cable-240w-power-output',
                    it: 'https://it.ecoflow.com/products/rapid-pro-c-c-cable-240w-power-output',
                    es: 'https://es.ecoflow.com/products/rapid-pro-c-c-cable-240w-power-output',
                    nl: 'https://es.ecoflow.com/products/rapid-pro-c-c-cable-240w-power-output',
                };
                return localMap[locale] || undefined;
            })(),
            linkBuy: '',
            linkAmazon: (() => {
                const localMap: any = {
                    jp: 'https://www.amazon.co.jp/dp/B0FCXVLDHG',
                    us: 'https://www.amazon.com/dp/B0FDGTY477?maas=maas_adg_AFA57C81A296B5FFDBB640F6938F5A52_afap_abs&ref_=aa_maas&tag=maas',
                    ca: 'https://www.amazon.ca/dp/B0FDGTY477?maas=maas_adg_97444D75594FFEC76751642F4F891BFC_afap_abs&ref_=aa_maas&tag=maas',
                };
                return localMap[locale] || undefined;
            })(),
            info: IS_EU
                ? [
                      { title: t('C_item7_info1_title'), desc: t('C_item7_info1_desc') },
                      { title: t('C_item7_info2_title'), desc: t('C_item7_info2_desc') },
                  ]
                : [
                      { title: t('C_item7_info1_title'), desc: t('C_item7_info1_desc') },
                      { title: t('C_item7_info2_title'), desc: t('C_item7_info2_desc') },
                      { title: t('C_item7_info3_title'), desc: t('C_item7_info3_desc') },
                  ],
        },
        {
            img: '',
            imgs: ['', img8[0], img8[1]],
            title: '',
            productId: (() => {
                const localMap: any = {
                    us: '15361641349193',
                    ca: '15126351413619',
                    jp: '14715336294767',
                };
                return localMap[locale] || undefined;
            })(),
            variantId: (() => {
                const localMap: any = {
                    us: '54593880064073',
                    ca: '52731620262259',
                    jp: '51993106383215',
                };
                return localMap[locale] || undefined;
            })(),
            linkMore: (() => {
                const localMap: any = {
                    us: 'https://us.ecoflow.com/products/ecoflow-rapid-pro-usb-c-to-usb-c-cable-240w-nylon',
                    ca: 'https://ca.ecoflow.com/products/ecoflow-rapid-pro-usb-c-to-usb-c-cable-240w-nylon',
                    jp: 'https://jp.ecoflow.com/products/rapid-pro-240w-usb-type-c-to-c',
                };
                return localMap[locale] || undefined;
            })(),
            linkBuy: '',
            linkAmazon: (() => {
                const localMap: any = {
                    us: 'https://www.amazon.com/dp/B0FF4XH2T4?maas=maas_adg_7A1D59F244FE41C5EA7A6DDC30D96892_afap_abs&ref_=aa_maas&tag=maas',
                    ca: 'https://www.amazon.ca/dp/B0FF4XH2T4?maas=maas_adg_E2A46EF5F5E06756FEBAE2CFA0C4213D_afap_abs&ref_=aa_maas&tag=maas',
                };
                return localMap[locale] || undefined;
            })(),
            // linkAmazon: IS_JP ? 'https://www.amazon.co.jp/dp/B0FCXRV9KZ' : '',
            info: [
                { title: t('C_item8_info1_title'), desc: t('C_item8_info1_desc') },
                { title: t('C_item8_info2_title'), desc: t('C_item8_info2_desc') },
            ],
        },
    ];
    if (IS_EU) {
        data.pop();
        if (IS_UK) {
            data = [...data.slice(0, 3), ...data.slice(4, 7)];
        }
        if (IS_IT) {
            data = [...data.slice(3)];
        }
        if (IS_NL) {
            data = [...data.slice(1)];
        }
    }

    if (IS_RW) {
        if (IS_PH) {
            data = data = [...data.slice(1, 4)];
        }
    }

    const [items, setItems] = useState<ItemType[]>(data);
    const [activeItem, setActiveItem] = useState<ItemType>(items[0]);
    const [active, setActive] = useState(0);
    const [swiper, setSwiper] = useState({
        realIndex: 0,
        slideTo: (_: number) => {},
        slidePrev: () => {},
        slideNext: () => {},
    } as SwiperType);
    const [groups, setGroups] = useState<ItemType[][]>([]);
    const [groupsH5, setGroupsH5] = useState<ItemType[][]>([]);
    const [activeId, setActiveId] = useState(items[0]?.variantId);

    const handleSlideChange = () => {
        setActive(swiper?.realIndex);
    };

    const handleHover = (item: ItemType) => {
        if (item.variantId !== activeItem.variantId) {
            setActiveItem(item);
            swiper?.slideTo(0);
            setActiveId('');
            setTimeout(() => {
                setActiveId(item.variantId);
            }, 200);
        }
    };

    const handleGTM = (num: number) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_detail_page',
            list_name: t('C_title'),
            button_name: `${num === 1 ? 'Left switch' : num === 2 ? 'Right switch' : 'none'}`,
            __btn_id1: `${css.section} .swiper-button-${num === 1 ? 'prev' : num === 2 ? 'next' : 'none'}`,
            __btn_id2: num === 1 ? '<' : '>',
            __btn_type: 'text',
        });
    };

    useEffect(() => {
        const tempArr = JSON.parse(JSON.stringify(data?.filter((item: any) => item?.productId && item?.variantId)));

        const getAsynItem = async (productId: string, variantId: string, k: number) => {
            return await new Promise(async (resolve, reject) => {
                try {
                    const res = await getSolarRecommand({ productId, variantId }, locale);
                    if (res?.code === '0') {
                        tempArr[k].productId = res?.data?.productId;
                        tempArr[k].price = res?.data?.price;
                        tempArr[k].compareAtPrice = res?.data?.compareAtPrice || res?.data?.price;
                        tempArr[k].inventoryQuantity = res?.data?.inventoryQuantity ?? 0;
                        tempArr[k].inventoryPolicy = res?.data?.inventoryPolicy || undefined;
                        tempArr[k].img = res?.data?.variantImg || res?.data?.img || '';
                        tempArr[k].title = res?.data?.skuTitle === 'Default Title' ? res?.data?.productTitle : res?.data?.skuTitle || '';
                        tempArr[k].imgs[0] = res?.data?.variantImg || res?.data?.img || '';
                        tempArr[k].linkBuy = `https://${locale}.ecoflow.com/cart/${tempArr[k].variantId}:1?channel=buy_button`;
                        tempArr[k].linkMore = `https://${locale}.ecoflow.com/products/${res?.data?.handle}`;
                        return resolve(res);
                    } else {
                        console.error(res?.message);
                        return reject(res?.message);
                    }
                } catch (e) {
                    console.error(e);
                    return reject(e);
                }
            });
        };

        const promises: any[] = tempArr?.map((item: any, k: number) => getAsynItem(item?.productId, item.variantId, k));
        Promise.all(promises)
            .then(() => {
                const newItems = JSON.parse(JSON.stringify(tempArr));
                setItems(newItems);
                if (typeof window !== 'undefined') {
                    if (IS_EU) {
                        if (IS_IT) {
                            setGroupsH5([[], newItems.slice(0, 3), newItems.slice(3)]);
                        } else {
                            setGroupsH5([newItems.slice(0, 3), newItems.slice(3)]);
                        }
                    } else {
                        setGroupsH5([newItems.slice(0, 4), newItems.slice(4)]);
                    }
                    setActiveItem(newItems[0]);
                    if (IS_UK) {
                        return setGroups([newItems.slice(0, 3), newItems.slice(3, 5), newItems.slice(5)]);
                    }
                    if (IS_IT) {
                        return setGroups([[], newItems.slice(0, 3), newItems.slice(3)]);
                    }
                    if (IS_EU) {
                        return setGroups([newItems.slice(0, 3), newItems.slice(3, 6), newItems.slice(6)]);
                    } else {
                        return setGroups([newItems.slice(0, 4), newItems.slice(4, 7), newItems.slice(7)]);
                    }
                }
            })
            .catch((e) => {
                console.error(e);
            });
    }, []);

    const buy = useScheduledContent({
        '2025/06/24 15:00:00 GMT+8': t('presale'),
        '2025/07/15 15:00:00 GMT+8': t('C_buy'),
    });

    const [showNotifyMe, setShowNotifyMe] = useState(false);

    return (
        <div id="rapid-pro-C" className={`rapid-pro-C ${css.section} GtmModuleData-section`} data-section-id={'rapid-pro-C'} data-section-title={t('C_title')}>
            <EcoSpace />
            <div className="info">
                <p className="info-title">{t('C_title')}</p>
            </div>
            <div className="pc">
                <Swiper
                    modules={[Navigation, EffectFade]}
                    fadeEffect={{
                        crossFade: true,
                    }}
                    effect="fade"
                    className="swiper"
                    spaceBetween={0}
                    slidesPerView={1}
                    initialSlide={0}
                    navigation={
                        activeItem?.imgs?.length > 1
                            ? {
                                  prevEl: `.rapid-pro-C .swiper-button-prev`,
                                  nextEl: `.rapid-pro-C .swiper-button-next`,
                              }
                            : false
                    }
                    onSwiper={(swipwerIns: SwiperType) => setSwiper(swipwerIns)}
                    // loop
                    onSlideChange={handleSlideChange}
                >
                    {activeItem?.imgs?.map((img: string, k: number) => (
                        <SwiperSlide className="swiper-item" key={k}>
                            <div className="swiper-item-img">
                                <EcoImg pc={img} />
                            </div>
                            <ul className={`swiper-item-info${activeId === activeItem.variantId ? ' active' : ''}`}>
                                {activeItem?.info?.map((sub: { title: string; desc: string }, m: number) => (
                                    <li className={`swiper-item-info-sub swiper-item-info-sub-${m + 1}`} key={`${k}_${m}`}>
                                        <div className="swiper-item-info-sub-info">
                                            <p className="swiper-item-info-sub-title">{sub.title}</p>
                                            <p className="swiper-item-info-sub-desc">{sub.desc}</p>
                                        </div>
                                        <p className="swiper-item-info-sub-line"></p>
                                    </li>
                                ))}
                            </ul>
                        </SwiperSlide>
                    ))}
                    {activeItem?.imgs?.length > 1 && (
                        <div className="swiper-button">
                            <button
                                className={`swiper-button-prev swiper-button-btn GtmModuleData-btn-text ${active <= 0 ? 'swiper-button-disabled' : ''}`}
                                onClick={() => handleGTM(1)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="44" height="45" viewBox="0 0 44 45" fill="none">
                                    <path
                                        d="M6.25977 0.719238L0.6875 6.60889V37.4536L6.25977 43.3442H37.0293L43.3125 37.4302V6.63232L37.0293 0.719238H6.25977Z"
                                        stroke="white"
                                        strokeWidth="1.375"
                                    />
                                    <mask id="path-2-inside-1_3127_21607_C1" fill="white">
                                        <path d="M17.4167 33.0317L28.4167 22.0317L17.4167 11.0317" />
                                    </mask>
                                    <path
                                        d="M18.389 34.004L17.4167 34.9763L15.4722 33.0317L16.4445 32.0595L18.389 34.004ZM28.4167 22.0317L29.389 21.0595C29.926 21.5964 29.926 22.467 29.389 23.004L28.4167 22.0317ZM16.4445 12.004L15.4722 11.0317L17.4167 9.08719L18.389 10.0595L16.4445 12.004ZM16.4445 32.0595L27.4445 21.0595L29.389 23.004L18.389 34.004L16.4445 32.0595ZM27.4445 23.004L16.4445 12.004L18.389 10.0595L29.389 21.0595L27.4445 23.004Z"
                                        fill="white"
                                        mask="url(#path-2-inside-1_3127_21607_C1)"
                                    />
                                </svg>
                            </button>
                            <button
                                className={`swiper-button-next swiper-button-btn GtmModuleData-btn-text ${
                                    active >= items.length - 1 ? 'swiper-button-disabled' : ''
                                }`}
                                onClick={() => handleGTM(2)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="44" height="45" viewBox="0 0 44 45" fill="none">
                                    <path
                                        d="M6.25977 0.719238L0.6875 6.60889V37.4536L6.25977 43.3442H37.0293L43.3125 37.4302V6.63232L37.0293 0.719238H6.25977Z"
                                        stroke="white"
                                        strokeWidth="1.375"
                                    />
                                    <mask id="path-2-inside-1_3127_21607_C2" fill="white">
                                        <path d="M17.4167 33.0317L28.4167 22.0317L17.4167 11.0317" />
                                    </mask>
                                    <path
                                        d="M18.389 34.004L17.4167 34.9763L15.4722 33.0317L16.4445 32.0595L18.389 34.004ZM28.4167 22.0317L29.389 21.0595C29.926 21.5964 29.926 22.467 29.389 23.004L28.4167 22.0317ZM16.4445 12.004L15.4722 11.0317L17.4167 9.08719L18.389 10.0595L16.4445 12.004ZM16.4445 32.0595L27.4445 21.0595L29.389 23.004L18.389 34.004L16.4445 32.0595ZM27.4445 23.004L16.4445 12.004L18.389 10.0595L29.389 21.0595L27.4445 23.004Z"
                                        fill="white"
                                        mask="url(#path-2-inside-1_3127_21607_C2)"
                                    />
                                </svg>
                            </button>
                        </div>
                    )}
                </Swiper>
                <ul className="groups">
                    {groups.map((group: ItemType[], k: number) => {
                        const IS_NO_OFF_TAG = items?.every((item: ItemType) => item?.compareAtPrice === item?.price);
                        if (group.length < 1) return null;
                        return (
                            <li className="group" key={k} style={{ width: `calc(100% / ${items?.length} * ${group.length})` }}>
                                <div className={`group-head${group?.find((item: ItemType) => item.variantId === activeItem.variantId) ? ' active' : ''}`}>
                                    <p className="group-head-title">{t(`C_series${k + 1}`)}</p>
                                    <p className={`group-head-line`}></p>
                                </div>
                                <ul className="group-items">
                                    {group.map((item: ItemType, m: number) => (
                                        <li
                                            className={`group-item${activeItem.variantId === item.variantId ? ' active' : ''}`}
                                            key={`${k}_${m}`}
                                            style={{ width: `calc(100% / ${group.length})` }}
                                            onMouseEnter={() => handleHover(item)}
                                        >
                                            <EcoLink
                                                className={`group-item-link GtmModuleData-btn-href link-${k}-${m}`}
                                                href={item.linkMore}
                                                dataLayer={{
                                                    event: 'uaEvent',
                                                    event_name: 'buy',
                                                    product_name: item.title,
                                                    position: 'Collections_Explore the RAPID Pro Lineup',
                                                    __btn_id1: `rapid-pro-C .link-${k}-${m}`,
                                                    __btn_id2: item.linkMore,
                                                    __btn_type: 'href',
                                                }}
                                            >
                                                <div className="group-item-img">
                                                    <EcoImg pc={item.img} />
                                                </div>
                                                <div className="group-item-info">
                                                    {!IS_NO_OFF_TAG && (
                                                        <div className={`group-item-info-label ${IS_NO_OFF_TAG ? 'no-all' : 'has-some'}`}>
                                                            {item.compareAtPrice === item.price
                                                                ? ''
                                                                : Math.round(((item.compareAtPrice - item.price) / item.compareAtPrice) * 100) +
                                                                  '%' +
                                                                  t('C_off')}
                                                        </div>
                                                    )}
                                                    <p className="group-item-info-title">{item.title}</p>
                                                    <div className="group-item-info-bot">
                                                        {activeItem.variantId !== item.variantId && (
                                                            <div className="group-item-info-price">
                                                                <p className="group-item-info-price-price">{FormatMoneyLocal(item?.price, locale) || ''}</p>
                                                                {item.compareAtPrice !== item.price && (
                                                                    <p className="group-item-info-price-compare">
                                                                        {FormatMoneyLocal(item.compareAtPrice, locale) || ''}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                        {activeItem.variantId === item.variantId && (
                                                            <>
                                                                {(item.inventoryQuantity > 0 || item?.inventoryPolicy === 'continue') && (
                                                                    <EcoLink
                                                                        className={`group-item-info-btn buy GtmModuleData-btn-href`}
                                                                        href={`https://${locale}.ecoflow.com/cart/${item.variantId}:1?channel=buy_button`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                        }}
                                                                        dataLayer={{
                                                                            event: 'uaEvent',
                                                                            event_name: 'checkout',
                                                                            button_name: `${IS_JP ? t('C_buy') : buy}_${item.title}`,
                                                                            __btn_id1: `rapid-pro-C .link-${k}-${m} .buy`,
                                                                            __btn_id2: `https://${locale}.ecoflow.com/cart/${item.variantId}:1?channel=buy_button`,
                                                                            __btn_type: 'href',
                                                                        }}
                                                                    >
                                                                        {IS_JP ? t('C_buy') : buy}
                                                                    </EcoLink>
                                                                )}
                                                                {item?.NotOnlineYet && (
                                                                    <button
                                                                        className="group-item-info-btn notify GtmModuleData-btn-text"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            setShowNotifyMe(true);
                                                                            gtm.push({
                                                                                __btn_id1: `${css.section} .link-${k}-${m} .notify`,
                                                                                __btn_id2: t('C_buy2') || t('C_notify'),
                                                                                __btn_type: 'text',
                                                                            });
                                                                        }}
                                                                    >
                                                                        {t('C_buy2') || t('C_notify')}
                                                                    </button>
                                                                )}
                                                                {item.inventoryQuantity <= 0 && item?.inventoryPolicy !== 'continue' && !item?.NotOnlineYet && (
                                                                    <button className="group-item-info-btn disabled">{t('C_sold_out')}</button>
                                                                )}
                                                                {(IS_JP || IS_NA) && item?.linkAmazon && !item?.isPreSale && !item?.NotOnlineYet && (
                                                                    <EcoLink
                                                                        className="group-item-info-btn amazon GtmModuleData-btn-href"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                        }}
                                                                        dataLayer={{
                                                                            event: 'uaEvent',
                                                                            event_name: 'buy_on_amazon',
                                                                            product_name: item.title,
                                                                            button_name: t('C_amazon'),
                                                                            list_name: t('C_title'),
                                                                            __btn_id1: `rapid-pro-C .link-${k}-${m} .amazon`,
                                                                            __btn_id2: item.linkAmazon,
                                                                            __btn_type: 'href',
                                                                        }}
                                                                        href={item.linkAmazon}
                                                                    >
                                                                        {Amazon}
                                                                        {t('C_amazon')}
                                                                    </EcoLink>
                                                                )}
                                                                {IS_JP && ((item?.linkAmazon && item?.isPreSale) || item?.NotOnlineYet) && (
                                                                    <button className="group-item-info-btn amazon GtmModuleData-btn-href disabled">
                                                                        {Amazon}
                                                                        {item?.isPreSale ? t('C_presale') : item?.NotOnlineYet ? t('C_buy2') : t('C_amazon')}
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </EcoLink>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        );
                    })}
                </ul>
            </div>
            <ul className="h5 rows">
                {groupsH5.map((group: ItemType[], k: number) => {
                    if (group.length < 1) return null;
                    return (
                        <li className="row" key={k}>
                            <div className="row-header">
                                <p className="row-header-title">{k === 0 ? t(`C_series${k + 1}`) : `${t(`C_series${k + 1}`)} & ${t(`C_series${k + 2}`)}`}</p>
                                <p className="row-header-line"></p>
                            </div>
                            <Swiper className="row-swiper" slidesPerView={'auto'} initialSlide={0} spaceBetween={12}>
                                {group.map((item: ItemType, m: number) => {
                                    const IS_NO_OFF_TAG = group?.every((item: ItemType) => item?.compareAtPrice === item?.price);
                                    return (
                                        <SwiperSlide className={`item ${IS_NO_OFF_TAG ? 'no-all' : 'has-some'}`} key={`${k}-${m}`}>
                                            <EcoLink
                                                className={`item-card GtmModuleData-btn-href link-${m}-${k}`}
                                                href={item.linkMore}
                                                dataLayer={{
                                                    event: 'uaEvent',
                                                    event_name: 'buy',
                                                    product_name: item.title,
                                                    position: 'Collections_Explore the RAPID Pro Lineup',
                                                    __btn_id1: `rapid-pro-C .link-${k}-${m}`,
                                                    __btn_id2: item.linkMore,
                                                    __btn_type: 'href',
                                                }}
                                            >
                                                <EcoImg className="item-img" pc={item.img} />
                                                {!IS_NO_OFF_TAG && (
                                                    <div className={`item-price-label ${IS_NO_OFF_TAG ? 'no-all' : 'has-some'}`}>
                                                        {item.compareAtPrice === item.price
                                                            ? ''
                                                            : Math.round(((item.compareAtPrice - item.price) / item.compareAtPrice) * 100) + '%' + t('C_off')}
                                                    </div>
                                                )}
                                                <p className="item-title">{item.title}</p>
                                                <div className="item-price">
                                                    <p className="item-price-price">{FormatMoneyLocal(item.price, locale)}</p>
                                                    <p className="item-price-compare">{FormatMoneyLocal(item.compareAtPrice, locale)}</p>
                                                </div>
                                                <div className="item-btns">
                                                    {(item.inventoryQuantity > 0 || item?.inventoryPolicy === 'continue') && (
                                                        <EcoLink
                                                            className={`item-btn buy GtmModuleData-btn-href`}
                                                            href={
                                                                IS_NA
                                                                    ? item.linkMore
                                                                    : `https://${locale}.ecoflow.com/cart/${item.variantId}:1?channel=buy_button`
                                                            }
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                            dataLayer={{
                                                                event: 'uaEvent',
                                                                event_name: 'checkout',
                                                                button_name: `${IS_JP ? t('C_buy') : buy}_${item.title}`,
                                                                __btn_id1: `rapid-pro-C .link-${k}-${m} .buy`,
                                                                __btn_id2: IS_NA
                                                                    ? item.linkMore
                                                                    : `https://${locale}.ecoflow.com/cart/${item.variantId}:1?channel=buy_button`,
                                                                __btn_type: 'href',
                                                            }}
                                                        >
                                                            {IS_JP ? t('buy_now') : buy}
                                                        </EcoLink>
                                                    )}
                                                    {item?.NotOnlineYet && (
                                                        <button
                                                            className="item-btn notify GtmModuleData-btn-text"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setShowNotifyMe(true);
                                                                gtm.push({
                                                                    __btn_id1: `rapid-pro-C .link-${k}-${m} .notify`,
                                                                    __btn_id2: t('C_buy2') || t('C_notify'),
                                                                    __btn_type: 'text',
                                                                });
                                                            }}
                                                        >
                                                            {t('C_buy2') || t('C_notify')}
                                                        </button>
                                                    )}
                                                    {item.inventoryQuantity <= 0 && item?.inventoryPolicy !== 'continue' && !item?.NotOnlineYet && (
                                                        <button className="item-btn disabled">{t('C_sold_out')}</button>
                                                    )}
                                                    {(IS_JP || IS_NA) && item?.linkAmazon && !item?.isPreSale && !item?.NotOnlineYet && (
                                                        <EcoLink
                                                            className="item-btn amazon GtmModuleData-btn-href"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                            dataLayer={{
                                                                event: 'uaEvent',
                                                                event_name: 'buy_on_amazon',
                                                                product_name: item.title,
                                                                button_name: t('C_amazon'),
                                                                list_name: t('C_title'),
                                                                __btn_id1: `rapid-pro-C .link-${k}-${m} .amazon`,
                                                                __btn_id2: item.linkAmazon,
                                                                __btn_type: 'href',
                                                            }}
                                                            href={item.linkAmazon}
                                                        >
                                                            {Amazon}
                                                            {t('C_amazon')}
                                                        </EcoLink>
                                                    )}
                                                    {IS_JP && item?.linkAmazon && !item?.isPreSale && !item?.NotOnlineYet && (
                                                        <EcoLink
                                                            className="item-btn amazon GtmModuleData-btn-href"
                                                            href={item.linkAmazon}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                            dataLayer={{
                                                                event: 'uaEvent',
                                                                event_name: 'buy_on_amazon',
                                                                product_name: item.title,
                                                                button_name: t('C_amazon'),
                                                                list_name: t('C_title'),
                                                                __btn_id1: `rapid-pro-C .link-${k}-${m} .amazon`,
                                                                __btn_id2: item.linkAmazon,
                                                                __btn_type: 'href',
                                                            }}
                                                        >
                                                            {Amazon}
                                                            {t('C_amazon')}
                                                        </EcoLink>
                                                    )}
                                                    {((item?.linkAmazon && item?.isPreSale) || item?.NotOnlineYet) && IS_JP && (
                                                        <button className="item-btn amazon disabled">
                                                            {Amazon}
                                                            {item?.isPreSale ? t('C_presale') : item?.NotOnlineYet ? t('C_buy2') : t('C_amazon')}
                                                        </button>
                                                    )}
                                                </div>
                                            </EcoLink>
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        </li>
                    );
                })}
            </ul>
            {t('C_tip') && <p className="tip">{t('C_tip')}</p>}

            {<Dialog open={showNotifyMe} onClose={() => setShowNotifyMe(false)} />}
        </div>
    );
};

export default C;
